// frontend/src/services/error/errorService.js

/**
 * Error Service - Centralized error handling
 */
class ErrorService {
  constructor() {
    this.listeners = [];
  }

  /**
   * Handle API errors consistently
   * @param {Error} error - The error object
   * @param {string} fallbackMessage - Fallback message if error details aren't available
   * @returns {Object} - Standardized error object
   */
  handleApiError(error, fallbackMessage = 'An error occurred') {
    // Extract the most relevant error message
    let message = fallbackMessage;
    let statusCode = null;
    let errorType = 'API_ERROR';
    let originalError = error;
    
    if (error.response) {
      // Server responded with an error status
      statusCode = error.response.status;
      
      // Try to get error detail from response data
      if (error.response.data) {
        if (typeof error.response.data === 'string') {
          message = error.response.data;
        } else if (error.response.data.detail) {
          message = error.response.data.detail;
        } else if (error.response.data.message) {
          message = error.response.data.message;
        } else if (error.response.data.error) {
          message = error.response.data.error;
        }
      }
      
      // Map common status codes to error types
      switch (statusCode) {
        case 400: errorType = 'BAD_REQUEST'; break;
        case 401: errorType = 'UNAUTHORIZED'; break;
        case 403: errorType = 'FORBIDDEN'; break;
        case 404: errorType = 'NOT_FOUND'; break;
        case 429: errorType = 'RATE_LIMITED'; break;
        case 500: errorType = 'SERVER_ERROR'; break;
        default: errorType = 'API_ERROR';
      }
    } else if (error.request) {
      // Request was made but no response received
      message = 'No response received from server. Please check your connection.';
      errorType = 'NETWORK_ERROR';
    } else {
      // Something else went wrong
      message = error.message || fallbackMessage;
    }
    
    // Log the error
    console.error(`[${errorType}] ${message}`, error);
    
    // Notify any global error listeners
    this._notifyListeners({
      type: errorType,
      message,
      statusCode,
      originalError
    });
    
    // Return standardized error object
    return {
      error: true,
      type: errorType,
      message,
      statusCode,
      originalError
    };
  }

  /**
   * Handle network errors
   * @param {Error} error - The error object
   * @param {string} fallbackMessage - Fallback message
   * @returns {Object} - Standardized error object
   */
  handleNetworkError(error, fallbackMessage = 'Network error') {
    const message = error.message || fallbackMessage;
    
    console.error(`[NETWORK_ERROR] ${message}`, error);
    
    this._notifyListeners({
      type: 'NETWORK_ERROR',
      message,
      originalError: error
    });
    
    return {
      error: true,
      type: 'NETWORK_ERROR',
      message,
      originalError: error
    };
  }

  /**
   * Handle authentication errors
   * @param {Error} error - The error object
   * @param {string} fallbackMessage - Fallback message
   * @returns {Object} - Standardized error object
   */
  handleAuthError(error, fallbackMessage = 'Authentication error') {
    let message = fallbackMessage;
    let errorType = 'AUTH_ERROR';
    
    if (error.message) {
      message = error.message;
    } else if (typeof error === 'string') {
      message = error;
    }
    
    console.error(`[${errorType}] ${message}`, error);
    
    this._notifyListeners({
      type: errorType,
      message,
      originalError: error
    });
    
    return {
      error: true,
      type: errorType,
      message,
      originalError: error
    };
  }

  /**
   * Add a global error listener
   * @param {Function} listener - Function to call when errors occur
   * @returns {Function} - Function to remove the listener
   */
  addErrorListener(listener) {
    this.listeners.push(listener);
    
    // Return function to remove listener
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * Notify listeners of errors
   * @param {Object} error - The error object
   * @private
   */
  _notifyListeners(error) {
    this.listeners.forEach(listener => {
      try {
        listener(error);
      } catch (err) {
        console.error("Error in error listener:", err);
      }
    });
  }
}

export default new ErrorService();